/** UI mirror of src/commissions/run-workflow.ts (keep in sync). */

export type CommissionRunApprovalRole = "inside_sales" | "operations" | "executive";

export type CommissionRunWorkflowFields = {
  locked_at: string | null;
  status: string;
  approved_inside_sales_at?: string | null;
  approved_inside_sales_by?: string | null;
  approved_operations_at?: string | null;
  approved_operations_by?: string | null;
  approved_executive_at?: string | null;
  approved_executive_by?: string | null;
  vendor_invoices_created_at?: string | null;
  statement_preview_sent_at?: string | null;
  statements_sent_at?: string | null;
};

export const COMMISSION_RUN_APPROVAL_SEGMENTS: Array<{
  id: "x1" | "x2" | "fully";
  role: CommissionRunApprovalRole;
  label: string;
  badgeLabel: string;
}> = [
  { id: "x1", role: "inside_sales", label: "x1", badgeLabel: "Inside sales" },
  { id: "x2", role: "operations", label: "x2", badgeLabel: "Operations" },
  { id: "fully", role: "executive", label: "Fully", badgeLabel: "Executive" },
];

export const COMMISSION_RUN_WORKFLOW_MAIN_STEPS = [
  { id: "draft", label: "Draft" },
  { id: "locked_for_review", label: "Locked for review" },
  { id: "approved", label: "Approved" },
  { id: "invoices_created", label: "Invoices created" },
  { id: "preview_sent", label: "Preview sent" },
  { id: "statements_sent", label: "Statements sent" },
] as const;

export function commissionRunApprovalAt(
  run: CommissionRunWorkflowFields,
  role: CommissionRunApprovalRole,
): string | null {
  switch (role) {
    case "inside_sales":
      return run.approved_inside_sales_at ?? null;
    case "operations":
      return run.approved_operations_at ?? null;
    case "executive":
      return run.approved_executive_at ?? null;
    default:
      return null;
  }
}

function commissionRunApprovalCount(run: CommissionRunWorkflowFields): number {
  let count = 0;
  for (const segment of COMMISSION_RUN_APPROVAL_SEGMENTS) {
    if (commissionRunApprovalAt(run, segment.role) != null) {
      count += 1;
    }
  }
  return count;
}

function isCommissionRunFullyApproved(run: CommissionRunWorkflowFields): boolean {
  return commissionRunApprovalCount(run) === COMMISSION_RUN_APPROVAL_SEGMENTS.length;
}

function isCommissionRunLockedForReview(run: { locked_at: string | null }): boolean {
  return run.locked_at != null;
}

export function commissionRunWorkflowMainStepIndex(run: CommissionRunWorkflowFields): number {
  if (!isCommissionRunLockedForReview(run)) {
    return 0;
  }
  if (run.statements_sent_at != null) {
    return COMMISSION_RUN_WORKFLOW_MAIN_STEPS.length;
  }
  if (run.statement_preview_sent_at != null) {
    return 5;
  }
  if (run.vendor_invoices_created_at != null) {
    return 4;
  }
  if (isCommissionRunFullyApproved(run)) {
    return 3;
  }
  if (commissionRunApprovalCount(run) > 0) {
    return 2;
  }
  return 1;
}

export function isCommissionRunWorkflowMainStepComplete(
  run: CommissionRunWorkflowFields,
  stepIndex: number,
): boolean {
  return stepIndex < commissionRunWorkflowMainStepIndex(run);
}

export function isCommissionRunWorkflowMainStepCurrent(
  run: CommissionRunWorkflowFields,
  stepIndex: number,
): boolean {
  return stepIndex === commissionRunWorkflowMainStepIndex(run);
}

export function isCommissionRunFullySignedOff(run: CommissionRunWorkflowFields): boolean {
  return isCommissionRunFullyApproved(run);
}
